/**
 * Scroll Lock Utilities
 * 
 * Manages page scroll locking during drag operations to prevent
 * unwanted scroll jumping. Works for both mouse wheel and touch.
 */

/**
 * Lock page scroll by fixing the body position
 * @param {React.MutableRefObject} scrollBlockerRef - Ref to store the wheel blocker function
 * @returns {void}
 */
export const lockPageScroll = (scrollBlockerRef) => {
  // Save current scroll position
  const scrollY = window.scrollY;
  
  // Lock the body
  document.body.style.position = 'fixed';
  document.body.style.top = `-${scrollY}px`;
  document.body.style.left = '0';
  document.body.style.right = '0';
  document.body.style.overflow = 'hidden';
  document.body.dataset.scrollLocked = 'true';
  document.body.dataset.scrollY = scrollY.toString();
  
  // Block wheel events that might cause scroll
  const blockWheel = (e) => {
    // Allow horizontal scroll (deltaX) but block vertical (deltaY)
    if (e.deltaY !== 0) {
      e.preventDefault();
      e.stopPropagation();
    }
  };
  scrollBlockerRef.current = blockWheel;
  window.addEventListener('wheel', blockWheel, { passive: false, capture: true });
  
  // Also block touchmove for touch devices
  const blockTouch = (e) => {
    // Only block if it's a vertical scroll gesture
    if (e.touches.length === 1) {
      e.preventDefault();
    }
  };
  document.body._blockTouch = blockTouch;
  document.addEventListener('touchmove', blockTouch, { passive: false });
};

/**
 * Unlock page scroll and restore previous position
 * @param {React.MutableRefObject} scrollBlockerRef - Ref containing the wheel blocker function
 * @returns {void}
 */
export const unlockPageScroll = (scrollBlockerRef) => {
  if (document.body.dataset.scrollLocked !== 'true') return;
  
  const scrollY = parseInt(document.body.dataset.scrollY || '0', 10);
  
  // Remove wheel blocker
  if (scrollBlockerRef.current) {
    window.removeEventListener('wheel', scrollBlockerRef.current, { capture: true });
    scrollBlockerRef.current = null;
  }
  
  // Remove touch blocker
  if (document.body._blockTouch) {
    document.removeEventListener('touchmove', document.body._blockTouch);
    delete document.body._blockTouch;
  }
  
  // Restore body styles
  document.body.style.position = '';
  document.body.style.top = '';
  document.body.style.left = '';
  document.body.style.right = '';
  document.body.style.overflow = '';
  delete document.body.dataset.scrollLocked;
  delete document.body.dataset.scrollY;
  
  // Restore scroll position
  window.scrollTo(0, scrollY);
};

/**
 * Cleanup scroll lock on unmount
 * Ensures scroll is properly restored if component unmounts during drag
 * @param {React.MutableRefObject} scrollBlockerRef - Ref containing the wheel blocker function
 * @returns {void}
 */
export const cleanupScrollLock = (scrollBlockerRef) => {
  // Remove wheel blocker if still attached
  if (scrollBlockerRef.current) {
    window.removeEventListener('wheel', scrollBlockerRef.current, { capture: true });
    scrollBlockerRef.current = null;
  }
  
  // Remove touch blocker if still attached
  if (document.body._blockTouch) {
    document.removeEventListener('touchmove', document.body._blockTouch);
    delete document.body._blockTouch;
  }
  
  // Ensure scroll is unlocked if component unmounts during drag
  if (document.body.dataset.scrollLocked === 'true') {
    const scrollY = parseInt(document.body.dataset.scrollY || '0', 10);
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.overflow = '';
    delete document.body.dataset.scrollLocked;
    delete document.body.dataset.scrollY;
    window.scrollTo(0, scrollY);
  }
};

